import * as React from 'react'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'

import Grid from '@mui/material/Grid'

import Container from '@mui/material/Container'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'
import LoadingButton from '@mui/lab/LoadingButton'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'

import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import StepButton from '@mui/material/StepButton'

import Typography from '@mui/material/Typography'

import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import AddLinkIcon from '@mui/icons-material/AddLink'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import TableViewOutlinedIcon from '@mui/icons-material/TableViewOutlined'
import ImportExportIcon from '@mui/icons-material/ImportExport'
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined'

import EditableSelect from '../EditableSelect'
import TextField from '@mui/material/TextField'

import {
    useContextsStore,
    selectOwnedContexts,
} from '../../stores/useContextsStore'
import { UserContext } from '../../UserContext'

import { useTheme } from '@mui/material/styles'

import { SubImportContext } from './SubImportContext'
import { DialogsContext, DialogsProvider } from '../../DialogsContext'

import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'
import FormControlLabel from '@mui/material/FormControlLabel'

import Switch from '@mui/material/Switch'

import axios from 'axios'
import qs from 'qs'

import {
    slugifyString,
    convertGPTResponsesToArray,
    isValidUrl,
    getUrlTypeAndCode,
} from '../../utils/index'


const steps = ['Start', 'Sources', 'Settings', 'Results']

const topHeader = {
    textMining: `Please, choose how you'd like to add your text:`,
    qualitativeImport: 'Please, paste your text or upload the files:',
    default: 'Develop an idea or analyze an existing text:',
}

const topByline = {
    textMining: 'Add Text, Keywords, or URLs here',
    qualitativeImport: 'Add your text, responses, or reviews here',
    default: 'Start with a thought, idea, an existing text, or a URL',
}

const editorHeader = {
    textMining: 'Text or Links',
    qualitativeImport: 'Plain Text',
    default: 'Text, Keywords, Links',
}

const editorByline = {
    textMining: 'Add plain text, keywords, or URLs here',
    qualitativeImport: 'Add or paste plain text here',
    default: 'Add plain text, keywords, or URLs here',
}

export default function TextDialog({
    presetContext = '',
    presetMode = 'default',
    open,
    onClose,
}) {
    const theme = useTheme() // hook to get the current theme

    const {
        activeImportDialog,
        setActiveImportDialog,
        presetSearchString,
        setPresetSearchString,
        openImportDialog,
        closeImportDialog,
    } = React.useContext(DialogsContext)

    // Shared across all import sources so the typed query persists when switching
    const searchString = presetSearchString
    const setSearchString = setPresetSearchString

    const {
        activeSubImportDialog,
        setActiveSubImportDialog,
    } = React.useContext(SubImportContext)

    const [selectedContext, setSelectedContext] = React.useState(presetContext)

    const [errorMessage, setErrorMessage] = React.useState(null)

    const [completedSteps, setCompletedSteps] = React.useState([])

    const [activeInputPanel, setActiveInputPanel] = React.useState(false)

    const { username } = React.useContext(UserContext)
    const contexts = useContextsStore(selectOwnedContexts)
    const contextNames = contexts.map(c => c.name)

    const [activeStep, setActiveStep] = React.useState(0)
    const [skipped, setSkipped] = React.useState(new Set())

    const isStepSkipped = step => {
        return skipped.has(step)
    }

    const handleNext = () => {
        let newSkipped = skipped
        if (isStepSkipped(activeStep)) {
            newSkipped = new Set(newSkipped.values())
            newSkipped.delete(activeStep)
        }

        setActiveStep(prevActiveStep => prevActiveStep + 1)
        setSkipped(newSkipped)
    }

    const handleSearchStringChange = event => {
        setSearchString(event.target.value)
    }

    const openTextEditImport = type => {
        setActiveSubImportDialog({})
        setActiveImportDialog(type)
    }

    React.useEffect(() => {
        if (activeStep != 1) return
        if (activeInputPanel != 'shortTextImport') return

        if (!searchString) {
            setErrorMessage(
                `Please, add a text that you would like to analyze and visualize.`
            )
            setActiveStep(0)
            return
        }

        const numberOfWords = searchString.split(' ')

        if (isValidUrl(searchString)) {
            const urlTypeAndCode = getUrlTypeAndCode(searchString)
            setPresetSearchString(searchString)
            switch (urlTypeAndCode.type) {
                case 'amazon':
                    setActiveImportDialog('amazonReviews')
                    break
                case 'youtube':
                    setActiveImportDialog('youtubeSubtitles')
                    break
                case 'wikipedia':
                    setActiveImportDialog('urls')
                    break
                case 'twitter':
                    setActiveImportDialog('twitter')
                    break
                case 'rss':
                    setActiveImportDialog('rss')
                    break
                default:
                    setActiveImportDialog('urls')
                    break
            }
            return
        }

        if (numberOfWords.length > 0 && numberOfWords.length < 10) {
            setPresetSearchString(searchString)
            setActiveImportDialog('shortTextImport')
            return
        }

        if (numberOfWords.length >= 10) {
            setPresetSearchString(searchString)
            setActiveImportDialog('textEditor')
            return
        }
    }, [activeStep])

    const handleStep = step => {
        setActiveStep(step)
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth={true}
                maxWidth="lg"
            >
                <DialogTitle
                    sx={{
                        padding: 4,
                        color: theme => theme.palette.text.primary,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {topHeader[presetMode]}
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Stepper
                    activeStep={activeStep}
                    sx={{ paddingLeft: 4, paddingRight: 4, paddingTop: 2 }}
                >
                    {steps.map((label, index) => {
                        const stepProps = {}
                        const labelProps = {}

                        if (isStepSkipped(index)) {
                            stepProps.completed = false
                        }
                        return (
                            <Step key={label} {...stepProps}>
                                <StepButton
                                    onClick={() => handleStep(index)}
                                    completed={(
                                        completedSteps.indexOf(index) >= 0
                                    ).toString()}
                                >
                                    <StepLabel
                                        {...labelProps}
                                        sx={{
                                            '& .MuiStepLabel-label': {
                                                fontSize: {
                                                    xs: '0.75rem',
                                                    sm: 'initial',
                                                },
                                            },
                                        }}
                                    >
                                        {label}
                                    </StepLabel>
                                </StepButton>
                            </Step>
                        )
                    })}
                </Stepper>

                {errorMessage && (
                    <Typography
                        sx={{
                            paddingRight: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                            paddingBottom: 0,
                        }}
                        color="error"
                        component="div"
                        gutterBottom
                    >
                        {errorMessage}
                    </Typography>
                )}

                {activeStep === 0 && (
                    <>
                        <DialogContent
                            sx={{
                                paddingRight: 4,
                                paddingLeft: 4,
                                paddingTop: 4,
                            }}
                        >
                            <Grid container spacing={8}>
                                <Grid item xs={12} sm={6}>
                                    {activeInputPanel === 'shortTextImport' && (
                                        <>
                                            <FormControl
                                                fullWidth
                                                sx={{ marginTop: 4 }}
                                            >
                                                <TextField
                                                    label={
                                                        topByline[presetMode]
                                                    }
                                                    value={searchString}
                                                    variant="outlined"
                                                    id="reactTextImportEditor"
                                                    onChange={
                                                        handleSearchStringChange
                                                    }
                                                    onBlur={() => {
                                                        if (
                                                            !searchString ||
                                                            searchString.length <
                                                                1
                                                        ) {
                                                            setActiveInputPanel(
                                                                false
                                                            )
                                                        }
                                                    }}
                                                    aria-describedby="searchString-helperText"
                                                    multiline
                                                    autoFocus
                                                    rows={8} // This can be any number that will fit within 200px height
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            height: '232px',
                                                            '& fieldset': {
                                                                borderWidth: 2,
                                                                borderColor: 'info.main',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderWidth: 2,
                                                                borderColor: 'info.dark',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderWidth: 2,
                                                            },
                                                            '& .MuiInputBase-inputMultiline': {
                                                                height: '232px',
                                                                overflowY:
                                                                    'auto',
                                                                padding:
                                                                    '0 !important',
                                                                boxShadow:
                                                                    'none !important',
                                                                border:
                                                                    'none !important',
                                                            },
                                                        },
                                                    }}
                                                />
                                            </FormControl>
                                        </>
                                    )}
                                    {activeInputPanel != 'shortTextImport' && (
                                        <Paper
                                            onClick={() =>
                                                setActiveInputPanel(
                                                    'shortTextImport'
                                                )
                                            }
                                            sx={{
                                                p: 2,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                textAlign: 'center',
                                                height: '200px',
                                                borderColor: 'primary.main',
                                                backgroundColor:
                                                    'background.default',
                                                cursor: 'pointer',
                                                marginTop: 4,
                                                opacity: activeInputPanel
                                                    ? 0.4
                                                    : 1,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                    gap: 2, // Adjust this value as needed for spacing
                                                }}
                                            >
                                                <TextFieldsIcon />
                                                {presetMode !=
                                                    'qualitativeImport' && (
                                                    <>
                                                        <Divider
                                                            orientation="vertical"
                                                            flexItem
                                                        />
                                                        <AddLinkIcon />
                                                    </>
                                                )}
                                                <Typography variant="subtitle1">
                                                    {editorHeader[presetMode]}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                sx={{ marginTop: 4 }}
                                                variant="body1"
                                            >
                                                {editorByline[presetMode]}
                                            </Typography>
                                        </Paper>
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Paper
                                        onClick={() =>
                                            openTextEditImport(
                                                'textFilesImport'
                                            )
                                        }
                                        sx={{
                                            p: 2,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            textAlign: 'center',
                                            height: '200px',
                                            borderColor: 'primary.main',
                                            backgroundColor:
                                                'background.default',
                                            cursor: 'pointer',
                                            marginTop: 4,
                                            opacity:
                                                activeInputPanel ==
                                                'shortTextImport'
                                                    ? 0.4
                                                    : 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                gap: 2, // Adjust this value as needed for spacing
                                            }}
                                        >
                                            <UploadFileOutlinedIcon />
                                            <Divider
                                                orientation="vertical"
                                                flexItem
                                            />
                                            <TableViewOutlinedIcon />
                                            <Typography variant="subtitle1">
                                                File Upload
                                            </Typography>
                                        </Box>
                                        <Typography
                                            sx={{ marginTop: 4 }}
                                            variant="body1"
                                        >
                                            Upload a .csv spreadsheet, .json,
                                            .pdf, or .txt. files
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </DialogContent>
                    </>
                )}

                <DialogActions
                    sx={{
                        paddingRight: 4,
                        paddingLeft: 4,
                        paddingBottom: 4,
                        paddingTop: 2,
                    }}
                >
                    {/* <Button onClick={onClose} variant="outlined">
                        Cancel
                    </Button> */}

                    <LoadingButton
                        color="info"
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        disabled={!searchString || !searchString.length > 0}
                        onClick={handleNext}
                        sx={{ px: 4, py: 1, fontSize: '0.95rem' }}
                    >
                        {activeStep === steps.length - 1 ? 'Visualize' : 'Next'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    )
}
